import React from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './components/AdminLayout';
import Login from './pages/Login/Login';
import Dashboard from './pages/Dashboard';
import Users from './pages/users/Users';
import AddUser from './pages/users/AddUser';
import EditUser from './pages/users/EditUser';
import ContactUs from './pages/contactUs/ContactUs';
import ViewContactUs from './pages/contactUs/ViewContactUs';
import Newsletter from './pages/newsletter/Newsletter';
import Services from './pages/services/Services';
import AddService from './pages/services/AddService';
import EditService from './pages/services/EditService';
import AddContent from './pages/content/AddContent';
import Contents from './pages/content/Contents';
import EditContent from './pages/content/EditContent';
import ViewContent from './pages/content/ViewContent';
import EmailTemplate from './pages/emailTemplate/EmailTemplate';
import EditEmailTemplate from './pages/emailTemplate/EditEmailTemplate';
import Settings from './pages/settings/Settings';
import Profile from './pages/profile/Profile';
import AddSlot from './pages/slots/AddSlot';
import EditSlot from './pages/slots/EditSlot';
import ViewSlot from './pages/slots/ViewSlot';

// New pages for comprehensive admin features
import Bookings from './pages/bookings/Bookings';
import AddBooking from './pages/bookings/AddBooking';
import EditBooking from './pages/bookings/EditBooking';
import ViewBooking from './pages/bookings/ViewBooking';
import BookingsTrash from './pages/bookings/BookingsTrash';
import ServiceCategories from './pages/services/ServiceCategories';
import SlotManagement from './pages/slots/Slots';
import PromoCodes from './pages/promo-codes/PromoCodes';
import AddPromoCode from './pages/promo-codes/AddPromoCode';
import DiagnosticCenters from './pages/diagnosticCenters/DiagnosticCenters';
import AddDiagnosticCenter from './pages/diagnosticCenters/AddDiagnosticCenter';
import EditDiagnosticCenter from './pages/diagnosticCenters/EditDiagnosticCenter';
import CenterServiceInstructions from './pages/centerServiceInstructions/CenterServiceInstructions';
import AddCenterServiceInstruction from './pages/centerServiceInstructions/AddCenterServiceInstruction';
import EditCenterServiceInstruction from './pages/centerServiceInstructions/EditCenterServiceInstruction';
import CenterServiceRequisitions from './pages/centerServiceRequisitions/CenterServiceRequisitions';
import AddCenterServiceRequisition from './pages/centerServiceRequisitions/AddCenterServiceRequisition';
import EditCenterServiceRequisition from './pages/centerServiceRequisitions/EditCenterServiceRequisition';
import RequisitionOptions from './pages/centerServiceRequisitions/RequisitionOptions';
import PromoAnalytics from './pages/PromoAnalytics';
import SEO from './pages/seo/SEO';
import SEOAdd from './pages/seo/AddSEO';
import SEOEdit from './pages/seo/EditSEO';
import EditPromoCode from './pages/promo-codes/EditPromoCode';
// The pages below are routed but intentionally NOT wired up here (admin
// Critical #3 / High #7, see AUDIT_FIX_LOG.md "Product decisions") - each
// is a static mock screen with zero API calls, indistinguishable from a
// real feature to an admin who reaches it. Files are untouched and still
// importable; only the routes that made them reachable are disabled, so
// nobody can land on a screen that silently does nothing. Re-enable by
// restoring the import + <Route> line once the real feature is built.
//
// import BookingsManagement from './pages/BookingsManagement';
// import PendingBookings from './pages/PendingBookings';
// import ApprovedBookings from './pages/ApprovedBookings';
// import CancelledBookings from './pages/CancelledBookings';
// import PatientData from './pages/PatientData';
// import WorkingHours from './pages/WorkingHours';
// import Holidays from './pages/Holidays';
// import ReportUpload from './pages/ReportUpload';
// import SentReports from './pages/SentReports';
// import EmailTemplates from './pages/EmailTemplates';
// import ContentManagement from './pages/ContentManagement';
// import HomeContent from './pages/HomeContent';
// import AboutContent from './pages/AboutContent';
// import ContactContent from './pages/ContactContent';
// import PoliciesContent from './pages/PoliciesContent';
// import MetaTags from './pages/MetaTags';
// import GoogleSearchConsole from './pages/GoogleSearchConsole';
// import Sitemap from './pages/Sitemap';
// import Statistics from './pages/Statistics';
// import MonthlyReports from './pages/MonthlyReports';
// import ExportData from './pages/ExportData';
// import Analytics from './pages/Analytics';
import Smtp from './pages/smtp/Smtp';
import EditSmtp from './pages/smtp/EditSmtp';
import NotificationLogs from './pages/notificationLogs/NotificationLogs';
import RadioReportParser from './pages/radioReport/RadioReportParser';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />

          {/* Bookings Management Routes */}
          {/* "bookings-old" (BookingsManagement) is a mock predecessor to
              the real Bookings.tsx below - disabled, see note near the
              imports. */}
          <Route path="bookings" element={<Bookings />} />
          <Route path="bookings/trash" element={<BookingsTrash />} />
          <Route path="bookings/add" element={<AddBooking />} />
          <Route path="bookings/edit/:id" element={<EditBooking />} />
          <Route path="bookings/view/:id" element={<ViewBooking />} />
          {/* bookings/pending, /approved, /cancelled (PendingBookings,
              ApprovedBookings, CancelledBookings) are mock - disabled. */}

          {/* User Management Routes */}
          <Route path="users" element={<Users />} />
          <Route path="users/add" element={<AddUser />} />
          <Route path="users/edit/:id" element={<EditUser />} />
          {/* users/patients (PatientData) is mock - disabled. */}

          {/* Contact Form Routes */}
          <Route path="contact-us" element={<ContactUs />} />
          <Route path="contact-us/view/:id" element={<ViewContactUs />} />

          {/* Newsletter Routes */}
          <Route path="newsletter" element={<Newsletter />} />

          {/* Service Management Routes */}
          <Route path="services" element={<Services />} />
          <Route path="services/add" element={<AddService />} />
          <Route path="services/edit/:id" element={<EditService />} />
          <Route path="services/categories" element={<ServiceCategories />} />

          {/* Diagnostic Centers Routes */}
          <Route path="diagnostic-centers" element={<DiagnosticCenters />} />
          <Route
            path="diagnostic-centers/add"
            element={<AddDiagnosticCenter />}
          />
          <Route
            path="diagnostic-centers/edit/:id"
            element={<EditDiagnosticCenter />}
          />

          {/* Center Service Instructions Routes */}
          <Route
            path="center-service-instructions"
            element={<CenterServiceInstructions />}
          />
          <Route
            path="center-service-instructions/add"
            element={<AddCenterServiceInstruction />}
          />
          <Route
            path="center-service-instructions/edit/:id"
            element={<EditCenterServiceInstruction />}
          />

          {/* Center Service Requisitions Routes */}
          <Route
            path="center-service-requisitions"
            element={<CenterServiceRequisitions />}
          />
          <Route
            path="center-service-requisitions/add"
            element={<AddCenterServiceRequisition />}
          />
          <Route
            path="center-service-requisitions/edit/:id"
            element={<EditCenterServiceRequisition />}
          />

          {/* Requisition Options Route */}
          <Route path="requisition-options" element={<RequisitionOptions />} />

          {/* Content Management Routes */}
          <Route path="content" element={<Contents />} />
          <Route path="content/add" element={<AddContent />} />
          <Route path="content/edit/:id" element={<EditContent />} />
          <Route path="content/view/:id" element={<ViewContent />} />

          {/* Slot Management Routes */}
          <Route path="slots" element={<SlotManagement />} />
          <Route path="slots/add" element={<AddSlot />} />
          <Route path="slots/edit/:id" element={<EditSlot />} />
          {/* slots/hours, slots/holidays (WorkingHours, Holidays) are mock - disabled. */}
          <Route path="slots/:id" element={<ViewSlot />} />

          {/* Promo Codes Routes */}
          <Route path="promo-codes" element={<PromoCodes />} />
          <Route path="promo-codes/add" element={<AddPromoCode />} />
          <Route path="promo-codes/edit/:id" element={<EditPromoCode />} />
          <Route path="promo-codes/analytics" element={<PromoAnalytics />} />

          {/* Reports & Delivery Routes */}
          {/* reports, reports/upload (ReportUpload) is fully mock - fake
              upload progress, fake "email sent" success, a hardcoded
              plaintext default report password. Disabled - admin
              Critical #3, see AUDIT_FIX_LOG.md.
              reports/sent (SentReports), reports/templates (EmailTemplates,
              the old mock one - not to be confused with the real
              EmailTemplate.tsx routed below at "email-templates") are
              also mock - disabled. */}

          {/* Radio Report Parser */}
          <Route path="radio-report-parser" element={<RadioReportParser />} />

          {/* Content Management Routes */}
          {/* This second "content" route (ContentManagement) was dead code
              even before being disabled here - React Router matches the
              first declared route, so "content" above (-> Contents.tsx)
              always won; this one was unreachable. content/home,
              content/about, content/contact, content/policies are mock -
              all disabled. */}

          {/* SEO Management Routes */}
          <Route path="seo" element={<SEO />} />
          <Route path="seo/add" element={<SEOAdd />} />
          <Route path="seo/edit/:id" element={<SEOEdit />} />
          {/* seo/meta, seo/console, seo/sitemap (MetaTags,
              GoogleSearchConsole, Sitemap) are mock - disabled. */}

          {/* Statistics & Export Routes */}
          {/* statistics, statistics/monthly, statistics/export,
              statistics/analytics are all mock - disabled. */}

          {/* Email Template Management Routes */}
          <Route path="email-templates" element={<EmailTemplate />} />
          <Route
            path="email-templates/edit/:id"
            element={<EditEmailTemplate />}
          />
          <Route path="notification-logs" element={<NotificationLogs />} />
          <Route path="smtp" element={<Smtp />} />
          <Route path="smtp/edit/:id" element={<EditSmtp />} />

          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
};

export default App;
